import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaChartBar, 
  FaRegCalendarAlt, 
  FaGlobeEurope, 
  FaRobot, 
  FaCog, 
  FaLightbulb,
  FaArrowRight,
  FaCheckCircle
} from 'react-icons/fa';
import API from '../api';
import './Instructions.css';

function Instructions() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInstructionsRead = async () => {
    setIsSubmitting(true);
    try {
      // Appeler l'endpoint pour marquer les instructions comme lues
      await API.post('/api/auth/mark-instructions-viewed');
      // Rediriger vers le dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error("Erreur lors du marquage des instructions comme lues:", error);
      // En cas d'erreur, rediriger quand même vers le dashboard
      navigate('/dashboard');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="global-container">
      <h2 className="instructions-title">Mode d'emploi ProspXion</h2>
      
      <div className="instructions-cards">
        <section className="instruction-card">
          <div className="card-header">
            <FaChartBar className="card-icon" />
            <h3>Tableau de bord</h3>
          </div>
          <div className="card-content">
            <p>Le tableau de bord vous offre une vue d'ensemble de toutes vos extractions et analyses. Consultez les graphiques pour suivre votre progression et identifier les tendances.</p>
            <img 
              src={`${process.env.PUBLIC_URL}/images/dashboard-preview.png`} 
              alt="Aperçu du tableau de bord" 
              className="feature-image"
              onError={(e) => e.target.style.display = 'none'}
            />
          </div>
        </section>

        <section className="instruction-card">
          <div className="card-header">
            <FaRegCalendarAlt className="card-icon" />
            <h3>F.R.E.S.H</h3>
          </div>
          <div className="card-content">
            <p className="feature-title">Fraîchement Recensées Et Sélectionnées Hebdomadairement</p>
            <p>Ce module vous permet d'extraire les entreprises nouvellement créées dans les départements de votre choix.</p>
            
            <div className="steps-container">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-text">Sélectionnez les départements qui vous intéressent</div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-text">Choisissez la période d'extraction (7, 30 ou 60 jours)</div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-text">Lancez l'extraction et téléchargez votre fichier</div>
              </div>
            </div>
          </div>
        </section>

        <section className="instruction-card">
          <div className="card-header">
            <FaGlobeEurope className="card-icon" />
            <h3>G.L.O.B.A.L</h3>
          </div>
          <div className="card-content">
            <p className="feature-title">Générateur de Leads Optimisés par Besoins d'Activités Locales</p>
            <p>Cette fonctionnalité vous permet de cibler précisément les entreprises selon leur emplacement et leur secteur d'activité.</p>
            
            <div className="steps-container">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-text">Sélectionnez les codes postaux souhaités en cliquant sur les départements</div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-text">Filtrez par codes NAF (secteurs d'activité)</div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-text">Lancez votre extraction et obtenez un fichier Excel prêt à l'emploi</div>
              </div>
            </div>
          </div>
        </section>

        <section className="instruction-card">
          <div className="card-header">
            <FaRobot className="card-icon" />
            <h3>ProspX - Assistant IA</h3>
          </div>
          <div className="card-content">
            <p>Votre assistant IA commercial vous aide à analyser vos données et à optimiser votre stratégie de prospection.</p>
            
            <div className="features-list">
              <div className="feature-item">
                <FaCheckCircle className="check-icon" />
                <span>Analysez vos données de prospection</span>
              </div>
              <div className="feature-item">
                <FaCheckCircle className="check-icon" />
                <span>Générez des scripts d'appels personnalisés</span>
              </div>
              <div className="feature-item">
                <FaCheckCircle className="check-icon" />
                <span>Obtenez des conseils pour améliorer votre taux de conversion</span>
              </div>
            </div>
          </div>
        </section>

        <section className="instruction-card">
          <div className="card-header">
            <FaCog className="card-icon" />
            <h3>Paramètres</h3>
          </div>
          <div className="card-content">
            <p>Dans cette section, vous pouvez :</p>
            <div className="features-list">
              <div className="feature-item">
                <FaCheckCircle className="check-icon" />
                <span>Mettre à jour votre profil et vos informations personnelles</span>
              </div>
              <div className="feature-item">
                <FaCheckCircle className="check-icon" />
                <span>Consulter les détails de votre licence et les départements autorisés</span>
              </div>
              <div className="feature-item">
                <FaCheckCircle className="check-icon" />
                <span>Modifier votre mot de passe</span>
              </div>
            </div>
          </div>
        </section>
      </div>
      
      <div className="tips-section">
        <div className="tips-header">
          <FaLightbulb className="tips-icon" />
          <h3>Astuces d'utilisation</h3>
        </div>
        <div className="tips-container">
          <div className="tip-card">
            <h4>Optimisez vos extractions</h4>
            <p>Privilégiez des filtres précis pour obtenir des leads de meilleure qualité plutôt que des listes trop volumineuses.</p>
            <div className="tip-footer">
              <FaArrowRight className="tip-arrow" />
            </div>
          </div>
          <div className="tip-card">
            <h4>Suivez vos résultats</h4>
            <p>Consultez régulièrement votre tableau de bord pour analyser l'efficacité de vos différentes extractions.</p>
            <div className="tip-footer">
              <FaArrowRight className="tip-arrow" />
            </div>
          </div>
          <div className="tip-card">
            <h4>Besoin d'aide ?</h4>
            <p>Utilisez notre page Contact pour toute question ou assistance supplémentaire.</p>
            <div className="tip-footer">
              <FaArrowRight className="tip-arrow" />
            </div>
          </div>
        </div>
      </div>

      <div className="instructions-confirm-button">
        <button 
          onClick={handleInstructionsRead}
          disabled={isSubmitting}
          className="instructions-done-button"
        >
          {isSubmitting ? "Traitement en cours..." : "J'ai compris, aller au tableau de bord"}
        </button>
      </div>
    </div>
  );
}

export default Instructions;
